import {identity} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {
  ConversationStatusesEnum,
  makeFindConversationActionContext,
} from '../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {setConversationsStatusAction} from '../../../store/data/conversations/statusConversationsActions';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {DestroyConversationDropdownItem} from '../viewer/conversation/actions/more/conversationsMoreDropdown';
import {
  ConversationsTrashButtonProps,
  ConversationsTrashCoordinatorModesEnum,
} from '../viewer/conversation/actions/trash/conversationsTrashCoordinator';
import {ConversationsTrashCoordinatorContainer} from '../viewer/conversation/actions/trash/conversationsTrashCoordinatorContainer';
import {DestroyConfirmationDialog} from '../viewer/conversation/topbar/conversationTopbarDestroy';
import {InboxesTopbarTooltipProps, makeInboxesTopbarTrashTooltipProps} from './inboxesTopbarCommon';
import {InboxesTopbarMainButton, InboxesTopbarMainButtonChevronProps} from './inboxesTopbarMainButton';

/*
 * Props.
 */

interface InboxesTopbarMainTrashProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
}

/*
 * Component.
 */

export const InboxesTopbarMainTrash: FC<InboxesTopbarMainTrashProps> = (props) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const conversationIds = useMemo(() => props.conversations.map((c) => c.id), [props.conversations]);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const onDestroyClick = props.isBulkActionRequired ? undefined : () => setIsConfirmationDialogOpen(true);

  const onDestroyConfirmClick = useCallback(() => {
    if (props.isBulkActionRequired) {
      return;
    }

    dispatch(
      setConversationsStatusAction(
        props.cellsContext,
        conversationIds,
        ConversationStatusesEnum.DESTROYED,
        makeFindConversationActionContext(props.cellsContext, props.conversations),
        {
          preventUndo: true,
        },
      ),
    );
  }, [conversationIds, props.isBulkActionRequired, dispatch, props.cellsContext, props.conversations]);

  const renderDropdown = (
    trashProps: ConversationsTrashButtonProps,
    popoverProps: PopoverTransferProps,
    tooltipProps: InboxesTopbarTooltipProps,
  ) => (
    <SimpleDropdown {...popoverProps}>
      <DropdownItem isDisabled={!trashProps.permissionResult.isAllowed} onSelect={trashProps.onClick}>
        <DropdownItemIcon
          iconName="inboxSmall"
          label={tooltipProps.mainButton}
          color={theme.greys.shade80}
          iconSize={16}
        />
        <DropdownItemContent>{tooltipProps.mainButton}</DropdownItemContent>
      </DropdownItem>
      <DestroyConversationDropdownItem
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        conversationsInboxes={props.conversationsInboxes}
        onDestroyClick={onDestroyClick}
      />
    </SimpleDropdown>
  );

  const renderButton = (trashProps: ConversationsTrashButtonProps) => {
    const tooltipProps = makeInboxesTopbarTrashTooltipProps(trashProps, props);

    const chevronProps: InboxesTopbarMainButtonChevronProps = {
      renderDropdown: (popoverProps) => renderDropdown(trashProps, popoverProps, tooltipProps),
      tooltipFirstLine: <Trans id="conversation-topbar-more">More options</Trans>,
    };
    const icon = trashProps.areAllTrashed ? (
      <Icon color={theme.palette.red.shade40} name="trashFilled" />
    ) : (
      <Icon name="trash" />
    );

    return (
      <InboxesTopbarMainButton
        permissionResult={trashProps.permissionResult}
        onClick={trashProps.onClick}
        keyboardAction={KeyboardActionsEnum.TRASH}
        tooltipFirstLine={tooltipProps.firstLine || tooltipProps.mainButton}
        tooltipSecondLine={tooltipProps.secondLine}
        chevronProps={props.cellsContext.inDrafts ? undefined : chevronProps}
        icon={icon}
      >
        {tooltipProps.mainButton}
      </InboxesTopbarMainButton>
    );
  };

  const maybeRenderDialog = () => {
    if (!isConfirmationDialogOpen) {
      return null;
    }

    return (
      <DestroyConfirmationDialog
        conversations={props.conversations}
        onDestroyClick={onDestroyConfirmClick}
        onRequestClose={() => setIsConfirmationDialogOpen(false)}
        wrap={identity}
      />
    );
  };

  return (
    <>
      <ConversationsTrashCoordinatorContainer
        panelId={props.panelId}
        cellsContext={props.cellsContext}
        inboxesParameters={props.inboxesParameters}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        mode={ConversationsTrashCoordinatorModesEnum.TRASH}
        renderButton={renderButton}
        skipTopbarPage
      />
      {maybeRenderDialog()}
    </>
  );
};
